import { useState } from 'react'
import type { Attributes, CatalogItem, DerivedStats, ItemUse, Loadout, Modifiers, Requirement, Reward, Slot } from '@mezha/api'
import { Emblem, type EmblemName } from '../ui/brand/Emblems'
import { RARITY_RU, type Rarity } from '../ui'

/** Shared vocabulary of the screens: slots, item kinds, modifiers, rewards. */

export const SLOTS: Array<{ slot: Slot; label: string; emblem: EmblemName }> = [
  { slot: 'weapon1', label: 'Оружие', emblem: 'slot.weapon' },
  { slot: 'weapon2', label: 'Второе', emblem: 'slot.offhand' },
  { slot: 'head', label: 'Голова', emblem: 'slot.head' },
  { slot: 'shoulder', label: 'Плечи', emblem: 'slot.shoulder' },
  { slot: 'body', label: 'Тело', emblem: 'slot.body' },
  { slot: 'gloves', label: 'Перчатки', emblem: 'slot.gloves' },
  { slot: 'belt', label: 'Пояс', emblem: 'slot.belt' },
  { slot: 'legs', label: 'Ноги', emblem: 'slot.legs' },
  { slot: 'accessory1', label: 'Оберег 1', emblem: 'slot.charm' },
  { slot: 'accessory2', label: 'Оберег 2', emblem: 'slot.charm' },
  { slot: 'accessory3', label: 'Оберег 3', emblem: 'slot.charm' },
  { slot: 'accessory4', label: 'Оберег 4', emblem: 'slot.charm' },
]

export const slotLabel = (slot: Slot | null | undefined): string => SLOTS.find(s => s.slot === slot)?.label ?? ''

/** Catalog slot category → the first loadout slot that accepts it. */
export const CATEGORY_SLOT: Record<string, Slot> = {
  weapon: 'weapon1', shield: 'weapon2', head: 'head', shoulder: 'shoulder', body: 'body', gloves: 'gloves', belt: 'belt', legs: 'legs', accessory: 'accessory1',
}

export const CATEGORY_RU: Record<string, string> = {
  weapon: 'оружие', shield: 'щит', head: 'голова', shoulder: 'плечи', body: 'тело', gloves: 'перчатки', belt: 'пояс', legs: 'ноги', accessory: 'оберег',
}

export const KIND_RU: Record<string, string> = {
  gear: 'Снаряжение', material: 'Материалы', quest: 'Квестовые', clue: 'Улики', cosmetic: 'Облик', consumable: 'Расходуемые',
}

export const KIND_EMBLEM: Record<string, EmblemName> = {
  gear: 'kind.gear', material: 'kind.material', quest: 'kind.quest', clue: 'kind.clue', cosmetic: 'kind.cosmetic', consumable: 'kind.consumable',
}

export const rarityRu = (r: string): string => RARITY_RU[r as Rarity] ?? r

/** Item picture from the server; an emblem of its kind when there is none. */
export function ItemArt({ item, size }: { item: Pick<CatalogItem, 'icon_url' | 'kind' | 'slot_category' | 'name'>; size?: number }) {
  const [failedURL, setFailedURL] = useState<string>()
  if (item.icon_url && failedURL !== item.icon_url) return <img src={item.icon_url} alt="" width={size} height={size} loading="lazy" onError={() => setFailedURL(item.icon_url)} />
  const emblem = item.kind === 'gear' ? SLOTS.find(s => s.slot === CATEGORY_SLOT[item.slot_category])?.emblem ?? 'kind.gear' : KIND_EMBLEM[item.kind] ?? 'nav.inventory'
  return <Emblem name={emblem} size={size ? Math.round(size * 0.6) : undefined} />
}

export const MOD_RU: Record<keyof Modifiers, string> = {
  str: 'Сила', vit: 'Стойкость', dex: 'Ловкость', wis: 'Мудрость', lck: 'Удача', kno: 'Знание',
  weapon_damage: 'Урон оружия', armor_class: 'Броня', spirit_guard: 'Защита духа', accuracy: 'Точность', feint: 'Обман',
  crushing: 'Сокрушение', resilience: 'Упорство', armor_pierce: 'Пробой брони', spirit_pierce: 'Пробой духа',
  crit_chance_bp: 'Шанс крита', hp_bonus: 'Макс. здоровье', mn_bonus: 'Макс. мана',
}

export const signed = (n: number): string => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : '0')

/** Flat HP/MN stack; attribute bonuses still have their own limits. */
export function ResourceBonusNote({ modifiers }: { modifiers: Modifiers }) {
  if (!['hp_bonus', 'mn_bonus', 'vit', 'kno', 'wis'].some(k => Number(modifiers[k as keyof Modifiers] ?? 0) !== 0)) return null
  return <p className="note itemcard__note">Бонусы к максимуму здоровья и маны складываются полностью. Прибавки от характеристик учитывают их пределы, травма уменьшает итоговое здоровье. Увеличение максимума не лечит и не восполняет ману.</p>
}

/** Basis points → "7,5 %". */
export const pct = (bp: number): string => `${(bp / 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} %`

export const modValue = (key: keyof Modifiers, v: number): string => (key === 'crit_chance_bp' ? `${v > 0 ? '+' : v < 0 ? '−' : ''}${pct(Math.abs(v))}` : signed(v))

/** Non-zero modifiers in a stable order. */
export function modList(m: Modifiers | Record<string, number> | undefined): Array<{ key: keyof Modifiers; label: string; value: number; text: string }> {
  if (!m) return []
  return (Object.keys(MOD_RU) as Array<keyof Modifiers>)
    .map(key => ({ key, value: Number((m as Record<string, number>)[key] ?? 0) }))
    .filter(x => x.value !== 0)
    .map(x => ({ ...x, label: MOD_RU[x.key], text: modValue(x.key, x.value) }))
}

/** What a consumable restores, as short lines: «Здоровье +40%». */
export function restoreList(u: ItemUse | null | undefined): Array<{ key: string; label: string; text: string }> {
  if (!u) return []
  if (u.speedup) return [{ key: 'speedup', label: 'Ускорение', text: 'Рыбалка или сбор' }]
  const out: Array<{ key: string; label: string; text: string }> = []
  if (u.hp_pct) out.push({ key: 'hp', label: 'Здоровье', text: `+${u.hp_pct}%` })
  if (u.mn_pct) out.push({ key: 'mn', label: 'Мана', text: `+${u.mn_pct}%` })
  if (u.st) out.push({ key: 'st', label: 'Выносливость', text: `+${u.st}` })
  return out
}

/** Russian plural of silver: «1 серебро» is not said; the game writes «N серебра». */
export const silverText = (n: number): string => `${n} серебра`

/** "было → станет" for two modifier sets: only keys that change. */
export function modDiff(before: Modifiers | undefined, after: Modifiers | undefined) {
  const keys = new Set<keyof Modifiers>([...modList(before).map(x => x.key), ...modList(after).map(x => x.key)])
  return [...keys].map(key => {
    const a = Number(before?.[key] ?? 0)
    const b = Number(after?.[key] ?? 0)
    return { key, label: MOD_RU[key], from: a, to: b, delta: b - a }
  }).filter(x => x.delta !== 0)
}

export const DERIVED_RU: Array<{ key: keyof DerivedStats; label: string; emblem: EmblemName; bp?: boolean }> = [
  { key: 'p', label: 'Сила удара', emblem: 'stat.power' },
  { key: 'p_spirit', label: 'Сила духа', emblem: 'stat.spirit_power' },
  { key: 'w', label: 'Урон оружия', emblem: 'stat.weapon_damage' },
  { key: 'a', label: 'Броня', emblem: 'stat.armor' },
  { key: 'a_spirit', label: 'Защита духа', emblem: 'stat.spirit_armor' },
  { key: 'accuracy_bp', label: 'Точность', emblem: 'stat.accuracy', bp: true },
  { key: 'evasion_bp', label: 'Уклонение', emblem: 'stat.evasion', bp: true },
  { key: 'parry_bp', label: 'Парирование', emblem: 'stat.parry', bp: true },
  { key: 'crit_bp', label: 'Крит', emblem: 'stat.crit', bp: true },
  { key: 'block_bp', label: 'Блок щитом', emblem: 'stat.block', bp: true },
  { key: 'status_power', label: 'Сила статусов', emblem: 'stat.status_power' },
  { key: 'status_resist', label: 'Стойкость к статусам', emblem: 'stat.status_resist' },
]

export const ATTRS: Array<{ key: 'str' | 'vit' | 'dex' | 'wis' | 'lck' | 'kno'; label: string; hint: string; emblem: EmblemName }> = [
  { key: 'str', label: 'Сила', hint: 'сила удара', emblem: 'attr.str' },
  { key: 'vit', label: 'Стойкость', hint: 'здоровье и броня', emblem: 'attr.vit' },
  { key: 'dex', label: 'Ловкость', hint: 'уклонение и точность', emblem: 'attr.dex' },
  { key: 'wis', label: 'Мудрость', hint: 'парирование и защита духа', emblem: 'attr.wis' },
  { key: 'lck', label: 'Удача', hint: 'шанс крита', emblem: 'attr.lck' },
  { key: 'kno', label: 'Знание', hint: 'мана и сила духа', emblem: 'attr.kno' },
]

/** A confirmed reward as a row of cells: silver, crystals, xp, items. */
export function RewardRow({ reward }: { reward: Reward }) {
  const parts: Array<{ key: string; emblem: EmblemName; value: string; label: string; trophy?: string; item?: CatalogItem; level?: number }> = []
  if (reward.xp) parts.push({ key: 'xp', emblem: 'res.xp', value: `+${reward.xp}`, label: 'опыта' })
  if (reward.silver) parts.push({ key: 'silver', emblem: 'res.silver', value: String(reward.silver), label: 'серебра' })
  if (reward.crystal) parts.push({ key: 'crystal', emblem: 'res.crystal', value: String(reward.crystal), label: 'кристаллов' })
  for (const [index, it] of reward.items.entries()) parts.push({ key: `${it.item_id}-${index}`, emblem: it.trophy_of ? 'reward.trophy' : 'reward.item', value: it.amount > 1 ? `×${it.amount}` : '', label: it.name, trophy: it.trophy_of || undefined, item: it.item ?? undefined, level: it.level_required })
  for (const ch of reward.saga ?? []) parts.push({ key: `saga-${ch.chapter_id}`, emblem: 'reward.saga', value: '', label: `Повесть: ${ch.title}` })
  if (parts.length === 0) return <p className="note">Без награды</p>
  return (
    <ul className="rewards" aria-label="Награда">
      {parts.map(p => (
        <li key={p.key} className={p.trophy ? 'reward reward--trophy' : 'reward'}>
          <span className={p.trophy ? 'cellslot r-epic' : 'cellslot r-common'} aria-hidden="true">{p.item ? <ItemArt item={p.item} size={46} /> : <Emblem name={p.emblem} size={30} />}</span>
          <b>{p.value || p.label}</b>{p.value ? p.label : ''}
          {!!p.level && <small>Уровень {p.level}</small>}
          {p.trophy && <small className="reward__trophy">Трофей: {p.trophy} — редкая добыча</small>}
        </li>
      ))}
    </ul>
  )
}

/** Reward in one line for quest pages and offers. */
export function rewardText(r: Reward | null | undefined): string {
  if (!r) return ''
  const parts: string[] = []
  if (r.xp) parts.push(`${r.xp} опыта`)
  if (r.silver) parts.push(`${r.silver} серебра`)
  if (r.crystal) parts.push(`${r.crystal} кристаллов`)
  for (const it of r.items) parts.push(it.amount > 1 ? `${it.name} ×${it.amount}` : it.name)
  for (const ch of r.saga ?? []) parts.push(`глава повести «${ch.title}»`)
  return parts.join(', ')
}

/** Weapon families of mezha-2 (combat_system_v2.md §4.1). */
export const FAMILY_RU: Record<string, string> = { sword: 'меч', axe: 'секира', spear: 'копьё', flail: 'кистень', bow: 'лук', staff: 'посох' }

/** «Сила 6» — an attribute requirement in words. */
export function requirementText(r: Requirement): string {
  return `${ATTRS.find(a => a.key === r.attr)?.label ?? r.attr} ${r.value}`
}

/** The first requirement of an item the hero's own attributes do not meet. */
export function unmetOf(item: Pick<CatalogItem, 'requires'>, attrs: Attributes | undefined): Requirement | null {
  if (!attrs) return null
  return (item.requires ?? []).find(r => attrs[r.attr] < r.value) ?? null
}

/** «нужна Сила 6 — у вас 5». */
export function unmetText(r: Requirement, attrs: Attributes | undefined): string {
  return `нужно: ${requirementText(r)}${attrs ? ` — у вас ${attrs[r.attr]}` : ''}`
}

/** Combat facts of a piece of gear for the item cards: family and hands,
 *  weight, the shield's block and the hard requirements. */
export function gearFacts(item: CatalogItem, attrs?: Attributes): Array<{ key: string; label: string; text: string; down?: boolean }> {
  if (item.kind !== 'gear') return []
  const out: Array<{ key: string; label: string; text: string; down?: boolean }> = []
  if (item.slot_category === 'weapon') {
    out.push({ key: 'family', label: 'Оружие', text: `${FAMILY_RU[item.family] ?? 'без семейства'} · ${item.hands === 2 ? 'двуручное' : 'одноручное'}` })
  }
  if (item.trophy_of) out.push({ key: 'trophy', label: 'Трофей', text: `только с противника «${item.trophy_of}»` })
  if (item.block_bp > 0) out.push({ key: 'block', label: 'Блок щитом', text: pct(item.block_bp) })
  if (item.weight > 0) out.push({ key: 'weight', label: 'Вес', text: String(item.weight) })
  for (const r of item.requires ?? []) {
    const low = attrs ? attrs[r.attr] < r.value : false
    out.push({ key: `req-${r.attr}`, label: 'Требует', text: `${requirementText(r)}${low && attrs ? ` — у вас ${attrs[r.attr]}` : ''}`, down: low })
  }
  return out
}

/** How a piece of gear compares with what the hero wears in its slot. */
export type Verdict = 'better' | 'worse' | 'mixed' | 'same' | 'free' | 'worn'
export const VERDICT_RU: Record<Verdict, string> = {
  better: 'лучше надетого', worse: 'хуже надетого', mixed: 'есть плюсы и минусы', same: 'как надетое', free: 'ячейка пуста', worn: 'уже надето',
}
export type CompareRow = { key: string; label: string; from: string; to: string; up: boolean; down: boolean }
export type Comparison = { slot: Slot; worn: CatalogItem | null; rows: CompareRow[]; verdict: Verdict; notes: string[] }

/** Weight of one point of a modifier in the verdict (a rough «how much is it worth»). */
const MOD_WEIGHT: Partial<Record<keyof Modifiers, number>> = {
  weapon_damage: 3, str: 2, vit: 2, dex: 2, wis: 2, lck: 2, kno: 2, crit_chance_bp: 0.02, hp_bonus: 0.2, mn_bonus: 0.2,
}

/** The slot a piece of gear would go to: the matching one; among the four
 *  charm slots — a free one, else the one with the weakest charm. */
export function slotFor(item: CatalogItem, loadout: Loadout): Slot | null {
  if (item.kind !== 'gear') return null
  if (item.slot_category !== 'accessory') return CATEGORY_SLOT[item.slot_category] ?? null
  const charms = loadout.slots.filter(s => s.categories.includes('accessory'))
  const free = charms.find(s => !s.entry)
  if (free) return free.slot
  const worth = (m: Modifiers) => modList(m).reduce((a, x) => a + x.value * (MOD_WEIGHT[x.key] ?? 1), 0)
  return [...charms].sort((a, b) => worth(a.entry!.item.modifiers) - worth(b.entry!.item.modifiers))[0]?.slot ?? null
}

/** Compares a piece of gear with the worn one: rows «now → then» of what
 *  changes and a verdict. Null for things that are not worn. */
export function compareWithWorn(item: CatalogItem, loadout: Loadout | undefined): Comparison | null {
  if (!loadout) return null
  const slot = slotFor(item, loadout)
  if (!slot) return null
  const worn = loadout.slots.find(s => s.slot === slot)?.entry?.item ?? null
  const notes: string[] = []
  if (item.hands === 2 && slot === 'weapon1' && loadout.slots.find(s => s.slot === 'weapon2')?.entry) notes.push('двуручное: вещь из второй руки снимется')
  if (worn?.id === item.id) return { slot, worn, rows: [], verdict: 'worn', notes }
  let score = 0
  const rows: CompareRow[] = modDiff(worn?.modifiers, item.modifiers).map(d => {
    score += d.delta * (MOD_WEIGHT[d.key] ?? 1)
    const fmt = (v: number) => (d.key === 'crit_chance_bp' ? pct(v) : String(v))
    return { key: d.key, label: d.label, from: fmt(d.from), to: fmt(d.to), up: d.delta > 0, down: d.delta < 0 }
  })
  const extra = (key: string, label: string, from: number, to: number, fmt: (v: number) => string, better: 1 | -1, weight: number) => {
    if (from === to) return
    score += (to - from) * better * weight
    rows.push({ key, label, from: fmt(from), to: fmt(to), up: (to - from) * better > 0, down: (to - from) * better < 0 })
  }
  extra('block', 'Блок щитом', worn?.block_bp ?? 0, item.block_bp, pct, 1, 0.01)
  extra('weight', 'Вес', worn?.weight ?? 0, item.weight, String, -1, 1.5)
  if (item.slot_category === 'weapon' && worn && (worn.family !== item.family || worn.hands !== item.hands)) {
    notes.push(`${FAMILY_RU[worn.family] ?? 'без семейства'} → ${FAMILY_RU[item.family] ?? 'без семейства'}${item.hands === 2 ? ', двуручное' : ''}`)
  }
  const ups = rows.filter(r => r.up).length
  const downs = rows.filter(r => r.down).length
  const verdict: Verdict = !worn ? 'free' : ups && !downs ? 'better' : downs && !ups ? 'worse' : ups && downs ? (Math.abs(score) < 1 ? 'mixed' : score > 0 ? 'better' : 'worse') : 'same'
  return { slot, worn, rows, verdict, notes }
}

/** «Сравнение с надетым»: the worn item, a table «now → then» and the verdict. */
export function CompareBlock({ cmp }: { cmp: Comparison }) {
  const slot = SLOTS.find(x => x.slot === cmp.slot)
  return (
    <section className={`compare compare--${cmp.verdict}`} aria-label="Сравнение с надетым">
      <div className="compare__head">
        <span className="compare__worn">{cmp.worn ? <ItemArt item={cmp.worn} size={32} /> : <Emblem name={slot?.emblem ?? 'slot.charm'} size={22} />}</span>
        <span className="compare__what">{cmp.worn ? <>Сейчас надето: <b>{cmp.worn.name}</b></> : <>Ячейка «{slot?.label ?? cmp.slot}» пуста</>}</span>
        <span className={`verdict verdict--${cmp.verdict}`}>{VERDICT_RU[cmp.verdict]}</span>
      </div>
      {cmp.rows.length > 0 && (
        <table className="compare__table">
          <thead><tr><th scope="col">Свойство</th><th scope="col">Сейчас</th><th scope="col">Станет</th></tr></thead>
          <tbody>
            {cmp.rows.map(r => (
              <tr key={r.key} className={r.up ? 'is-up' : r.down ? 'is-down' : undefined}>
                <th scope="row">{r.label}</th><td>{r.from}</td><td>{r.to}<i aria-hidden="true">{r.up ? ' ▲' : r.down ? ' ▼' : ''}</i></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {cmp.notes.map(n => <p key={n} className="compare__note">{n}</p>)}
    </section>
  )
}
